import type { CurrencyCode } from '#graphql-operations'

/**
 * The badge text for a product's own sale — "Sleva 20%". One definition, so
 * the collection grids, the homepage rows, the sale page, the search tiles and
 * the product page cannot word the same cut five different ways.
 *
 * The noun leads and the depth follows in every locale, rather than deferring
 * to each language's natural order: the badge holds the same shape on every
 * card no matter which storefront the customer is on. Cased as the locale file
 * writes it, so the pill reads as ordinary copy rather than a shout.
 *
 * The noun names the price cut itself, not the campaign around it — cs "Sleva",
 * pl "Rabat", de "Rabatt" — so it stays true on a product discounted outside
 * any sale event. It is borrowed from the order summary's own label, which is
 * the same word the customer meets again at checkout; keep the two in step if
 * that copy ever changes.
 *
 * Empty when the product isn't discounted, which is the signal for callers to
 * render no badge at all. A campaign coupon stacks on top of this and gets its
 * own badge beside it — see `useCouponBadge`.
 */
export function useSaleBadge() {
  const { t } = useI18n()

  return (savedPercent?: number | null): string | undefined =>
    savedPercent
      ? `${t('order.summary.discount.label')} ${savedPercent}%`
      : undefined
}

/**
 * The same badge worded in money — "Sleva 686 Kč" — for when a percentage would
 * lie. Percentages stop adding up the moment a campaign coupon stacks on a
 * sale: 20 % off a price already cut by 20 % is 36 %, not 40 %, and the badge
 * has no room to explain which of the two it means. The amount saved has no
 * such ambiguity, so a coupon switches every badge over to it — see
 * `useCouponCardPricing`, which does the arithmetic.
 *
 * Empty for a non-positive saving, matching `useSaleBadge` above.
 */
export function useSavedAmountBadge() {
  const { t } = useI18n()
  const priceFormatter = usePriceFormatter()

  return (savedAmount?: number | null, currency?: CurrencyCode | null): string | undefined =>
    savedAmount && savedAmount > 0 && currency
      ? `${t('order.summary.discount.label')} ${priceFormatter(savedAmount, currency)}`
      : undefined
}
